<template>
  <div class="container-fluid team-management">
    <!-- Success and Error Messages -->
    <div
        v-if="successMessage"
        class="alert alert-success alert-dismissible fade show fixed-alert"
        role="alert"
    >
      {{ successMessage }}
      <button type="button" class="btn-close" @click="successMessage = ''"></button>
    </div>

    <div
        v-if="errorMessage"
        class="alert alert-danger alert-dismissible fade show fixed-alert"
        role="alert"
    >
      {{ errorMessage }}
      <button type="button" class="btn-close" @click="errorMessage = ''"></button>
    </div>

    <!-- Page Title -->
    <div class="mt-4 mb-2 text-center">
      <h2>{{ $t('teamManagement') }}</h2>
    </div>

    <div class="d-flex main-content">
      <!-- LEFT SECTION WITH TABS -->
      <div class="left-section">
        <div class="tab-container">
          <div
              class="tab reasons-tab"
              :class="{ active: activeSection === 'team' }"
              @click="setActiveSection('team')"
          >
            {{ $t('teamMembers') || 'Team members' }}
          </div>
          <div
              class="tab statistics-tab"
              :class="{ active: activeSection === 'absences' }"
              @click="setActiveSection('absences')"
          >
            {{ $t('teamAbsences') || 'Absences' }}
          </div>
        </div>

        <!-- TAB 1: TEAM MEMBERS -->
        <div v-if="activeSection === 'team'">
          <!-- Direct Reports header + Choose all / Clear -->
          <div class="d-flex align-items-center justify-content-between">
            <h4 class="mb-0">{{ $t('directReports') }}</h4>

            <button
                type="button"
                class="btn btn-sm btn-outline-secondary"
                :disabled="!reports.length"
                @click="toggleDirectReportsSelection"
            >
              {{
                allDirectReportsSelected
                    ? ($t('clearSelection') || 'Clear')
                    : ($t('chooseAll') || 'Choose all')
              }}
            </button>
          </div>

          <ul class="list-group mt-2">
            <li
                v-for="user in reports"
                :key="user.id"
                class="list-group-item d-flex align-items-center"
            >
              <input
                  type="checkbox"
                  :value="user"
                  v-model="selectedUsers"
                  class="form-check-input me-2"
                  @click.stop
              />
              <router-link
                  class="user-link"
                  :to="{ name: 'UserDetail', params: { id: user.id } }"
              >
                {{ user.first_name }} {{ user.last_name }}
              </router-link>
            </li>
          </ul>

          <!-- All Reports -->
          <h4 class="mt-4">{{ $t('allReports') }}</h4>
          <ul class="list-group">
            <li
                v-for="user in allReports"
                :key="user.id"
                class="list-group-item d-flex align-items-center"
            >
              <input
                  type="checkbox"
                  :value="user"
                  v-model="selectedUsers"
                  class="form-check-input me-2"
              />
              <span>{{ user.first_name }} {{ user.last_name }}</span>
            </li>
          </ul>
        </div>

        <!-- TAB 2: PENDING ABSENCES (ONLY FOR SELECTED USERS) -->
        <div v-else>
          <h4 class="mb-3">
            {{ $t('pendingAbsences') || 'Pending absences' }}
          </h4>

          <div v-if="!selectedUsers.length" class="text-muted">
            {{
              $t('selectUsersToSeeAbsences') ||
              'Select one or more users to see their pending absences.'
            }}
          </div>

          <div v-else-if="filteredPendingAbsences.length">
            <table class="table table-sm align-middle">
              <tbody>
              <tr
                  v-for="absence in filteredPendingAbsences"
                  :key="absence.id"
                  class="absence-card"
                  @click="focusAbsenceOnCalendar(absence)"
              >
                <td colspan="4">
                  <!-- ROW 1: USER NAME -->
                  <div class="absence-user fw-bold clickable-row">
                    {{ absence.user.first_name }} {{ absence.user.last_name }}
                  </div>

                  <!-- ROW 2: TYPE -->
                  <div class="absence-type text-purple">
                    {{ absence.absence_type?.name || absence.absence_type_name }}
                  </div>

                  <!-- ROW 3: DATE -->
                  <div class="absence-date text-muted small">
                    {{ formatDate(absence.absence_date) }}
                  </div>

                  <!-- ROW 4: BUTTONS -->
                  <div class="absence-buttons text-end mt-2">
                    <button
                        class="btn btn-success btn-sm me-2"
                        @click.stop="approveAbsence(absence)"
                    >
                      {{ $t('approve') }}
                    </button>
                    <button
                        class="btn btn-danger btn-sm"
                        @click.stop="declineAbsence(absence)"
                    >
                      {{ $t('decline') }}
                    </button>
                  </div>
                </td>
              </tr>
              </tbody>
            </table>
          </div>

          <div v-else class="text-muted">
            {{
              $t('noPendingAbsencesForSelection') ||
              'No pending absences for selected users.'
            }}
          </div>
        </div>
      </div>

      <!-- RIGHT SECTION: CALENDAR ALWAYS VISIBLE -->
      <div class="right-section">
        <YearlyCalendar
            :selected-users="selectedUsers"
            :absences="absences"
            :holidays="holidays"
            :available-years="availableYears"
            :current-year-index="currentYearIndex"
            @change-year="changeYear"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { useMainStore } from "@/store";
import axios from "axios";
import YearlyCalendar from "@/views/vacation/YearlyCalendar.vue";

export default {
  name: "TeamManagement",
  components: {YearlyCalendar},
  data() {
    return {
      activeSection: "team", // 'team' | 'absences'

      reports: [], // Direct reports
      allReports: [], // All reports (direct + indirect)
      selectedUsers: [],

      pendingAbsences: [], // flat list of pending absences (direct reports)
      absences: [],
      holidays: [],
      availableYears: [{id: null, year: new Date().getFullYear()}],
      currentYearIndex: 0,

      successMessage: "",
      errorMessage: "",
    };
  },
  computed: {
    store: () => useMainStore(),
    filteredPendingAbsences() {
      if (!this.selectedUsers?.length) return [];
      const selectedIds = new Set(this.selectedUsers.map((u) => u.id));
      return (this.pendingAbsences || []).filter(
          (a) => a?.user?.id && selectedIds.has(a.user.id)
      );
    },
    allDirectReportsSelected() {
      if (!this.reports.length) return false;
      const selectedIds = new Set(this.selectedUsers.map((u) => u.id));
      return this.reports.every((u) => selectedIds.has(u.id));
    },
  },
  mounted() {
    this.fetchYearSettings();
    this.fetchReports();
    this.fetchAllReports();
    this.fetchPendingAbsences();
  },
  watch: {
    selectedUsers: "fetchAbsences",
    currentYearIndex: "fetchAbsences",
  },
  methods: {
    setActiveSection(section) {
      this.activeSection = section;
      if (section === "absences") {
        this.fetchPendingAbsences();
      }
    },

    toggleDirectReportsSelection() {
      if (!this.reports.length) return;

      const reportIds = new Set(this.reports.map((u) => u.id));

      if (this.allDirectReportsSelected) {
        // Clear only direct reports from selection, keep other selected users (e.g., indirect)
        this.selectedUsers = this.selectedUsers.filter((u) => !reportIds.has(u.id));
        return;
      }

      // Add all direct reports (merge, no duplicates)
      const byId = new Map(this.selectedUsers.map((u) => [u.id, u]));
      this.reports.forEach((u) => byId.set(u.id, u));
      this.selectedUsers = Array.from(byId.values());
    },

    async fetchReports() {
      try {
        const userId = this.store.user.id;
        const response = await axios.get(`/api/v1/admin/users/${userId}/direct-reports/`);
        this.reports = response.data.results || response.data;
      } catch (error) {
        this.errorMessage = "Failed to fetch direct reports.";
        setTimeout(() => (this.errorMessage = ""), 3000);
      }
    },

    async fetchAllReports() {
      try {
        const userId = this.store.user.id;
        const response = await axios.get(`/api/v1/admin/users/${userId}/all-reports/`);
        this.allReports = response.data.results || response.data;
      } catch (error) {
        this.errorMessage = "Failed to fetch all reports.";
        setTimeout(() => (this.errorMessage = ""), 3000);
      }
    },


    async fetchYearSettings() {
      try {
        const response = await axios.get(`/api/vacation/year-settings/`.replace("/api", "/api/v1"));
        this.availableYears = response.data.results;
        this.availableYears.sort((a, b) => a.year - b.year);

        this.currentYearIndex = this.availableYears.findIndex(
            (yearSetting) => yearSetting.year === new Date().getFullYear()
        );
        if (this.currentYearIndex === -1) this.currentYearIndex = 0;

        const yearId = this.availableYears[this.currentYearIndex]?.id;
        if (yearId) {
          await this.fetchHolidays(yearId);
          await this.fetchAbsences();
        }
      } catch (error) {
        this.errorMessage = "Failed to fetch year settings.";
        setTimeout(() => (this.errorMessage = ""), 3000);
      }
    },

    changeYear(newYearIndex) {
      this.currentYearIndex = newYearIndex;
      const yearId = this.availableYears[newYearIndex]?.id;
      if (yearId) {
        this.fetchHolidays(yearId);
        this.fetchAbsences();
      }
    },

    async fetchHolidays(yearId) {
      try {
        const response = await axios.get(`/api/v1/vacation/holidays?year_id=${yearId}`);
        this.holidays = response.data.results || response.data;
      } catch (error) {
        this.errorMessage = `Failed to fetch holidays for YearSetting ID ${yearId}.`;
        setTimeout(() => (this.errorMessage = ""), 3000);
      }
    },

    async fetchAbsences() {
      try {
        const userIds = this.selectedUsers.map((u) => u.id).join(",");
        const yearId = this.availableYears[this.currentYearIndex]?.id;
        if (!userIds || !yearId) {
          this.absences = [];
          return;
        }

        const response = await axios.get(`/api/v1/vacation/absences/`, {
          params: {user_ids: userIds, year_id: yearId},
        });

        const raw = response.data.results || response.data;
        this.absences = raw.filter((a) => a.status !== "declined");
      } catch (error) {
        this.errorMessage = "Failed to fetch absences.";
        setTimeout(() => (this.errorMessage = ""), 3000);
      }
    },

    async fetchPendingAbsences() {
      try {
        const response = await axios.get("/api/v1/vacation/absences/pending-direct-reports/");
        this.pendingAbsences = response.data.results || response.data || [];
      } catch (error) {
        this.errorMessage = "Failed to fetch pending absences.";
        setTimeout(() => (this.errorMessage = ""), 3000);
      }
    },

    formatDate(dateStr) {
      if (!dateStr) return "";
      const d = new Date(dateStr);
      return d.toLocaleDateString();
    },

    focusAbsenceOnCalendar(absence) {
      const user = absence.user;
      if (!user) return;

      const existing = this.selectedUsers.find((u) => u.id === user.id);
      this.selectedUsers = existing ? [existing] : [user];

      if (absence.year_settings && absence.year_settings.year) {
        const idx = this.availableYears.findIndex((y) => y.year === absence.year_settings.year);
        if (idx !== -1 && idx !== this.currentYearIndex) {
          this.changeYear(idx);
        }
      }
    },

    async approveAbsence(absence) {
      try {
        await axios.patch(`/api/v1/vacation/absences/${absence.id}/`, {status: "approved"});

        this.successMessage = "Absence approved.";
        setTimeout(() => (this.successMessage = ""), 3000);

        await this.fetchPendingAbsences();
        await this.fetchAbsences();
      } catch (error) {
        this.errorMessage = this.getErrorMessage(error);
        setTimeout(() => (this.errorMessage = ""), 5000);
      }
    },

    async declineAbsence(absence) {
      try {
        await axios.patch(`/api/v1/vacation/absences/${absence.id}/`, {status: "declined"});

        this.successMessage = "Absence declined.";
        setTimeout(() => (this.successMessage = ""), 3000);

        await this.fetchPendingAbsences();
        await this.fetchAbsences();
      } catch (error) {
        this.errorMessage = this.getErrorMessage(error);
        setTimeout(() => (this.errorMessage = ""), 5000);
      }
    },

    getErrorMessage(error) {
      if (!error.response || !error.response.data) {
        return "An unexpected error occurred. Please try again.";
      }

      const errorData = error.response.data;

      if (Array.isArray(errorData)) {
        return (
            errorData.flatMap((errObj) => errObj.non_field_errors || []).join(" ") ||
            "An error occurred."
        );
      }

      if (typeof errorData === "string") return errorData;
      if (errorData.detail) return errorData.detail;
      if (errorData.non_field_errors) return errorData.non_field_errors.join(" ");

      return Object.values(errorData).flat().join(" ") || "An error occurred.";
    },
  },
};
</script>

<style scoped>
.list-group-item {
  cursor: pointer;
}

.list-group-item input[type="checkbox"] {
  margin-right: 10px;
}

.user-link {
  cursor: pointer;
  text-decoration: none;
  color: black;
}

.user-link:hover {
  text-decoration: underline;
}

.clickable-row {
  cursor: pointer;
}

.clickable-row:hover {
  background-color: #f5f5f5;
}
</style>
